import React, { useEffect, useState } from 'react';
import { Button, Modal, Typography } from 'antd';
import OperatorPinField from './OperatorPinField';
import { useGetUsersQuery } from '../../redux/api/users';

const { Text } = Typography;

interface IProps {
	open: boolean;
	title: string;
	description: string;
	label: string;
	onCancel: () => void;
	/** May return a Promise; Continue stays loading until it settles. */
	onConfirm: (userId: number, pin: string) => void | Promise<void>;
	/**
	 * Role group(s) accepted for this PIN (default: Machine Operators).
	 * Split / receiving flows pass Material Receivers.
	 */
	expectedGroups?: string[];
}

/**
 * Asks who is doing an action and shows that person's name once their PIN
 * resolves. Confirm stays available only after the identity is on screen.
 * While onConfirm is in flight, Continue is loading/disabled so a double-click
 * cannot submit twice (PTB-7313 review).
 */
const OperatorPinModal: React.FC<IProps> = ({
	open,
	title,
	description,
	label,
	onCancel,
	onConfirm,
	expectedGroups = ['Machine Operators'],
}) => {
	const groupName = expectedGroups[0] ?? 'Machine Operators';
	const { data: userData } = useGetUsersQuery(
		{ groups__name: groupName },
		{ skip: !open }
	);
	const [operatorId, setOperatorId] = useState<number | undefined>();
	const [operatorPin, setOperatorPin] = useState<string | undefined>();
	const [showError, setShowError] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (!open) {
			setOperatorId(undefined);
			setOperatorPin(undefined);
			setShowError(false);
			setSubmitting(false);
		}
	}, [open]);

	const handleConfirm = async () => {
		if (submitting) return;
		if (!operatorId || !operatorPin) {
			setShowError(true);
			return;
		}
		setSubmitting(true);
		try {
			await Promise.resolve(onConfirm(operatorId, operatorPin));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal
			open={open}
			title={title}
			onCancel={submitting ? undefined : onCancel}
			maskClosable={!submitting}
			closable={!submitting}
			centered
			destroyOnClose
			footer={
				<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
					<Button onClick={onCancel} disabled={submitting}>
						Cancel
					</Button>
					<Button
						type="primary"
						data-testid="operator-pin-continue"
						onClick={handleConfirm}
						loading={submitting}
						disabled={submitting}
					>
						Continue
					</Button>
				</div>
			}
		>
			<Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
				{description}
			</Text>
			<OperatorPinField
				userData={userData?.results || []}
				expectedGroups={expectedGroups}
				label={label}
				value={operatorId}
				disabled={submitting}
				onResolved={(userId, _info, pin) => {
					setOperatorId(userId);
					setOperatorPin(pin);
					if (userId) setShowError(false);
				}}
				showError={showError}
			/>
		</Modal>
	);
};

export default OperatorPinModal;
